import {
  ActivityIndicator,
  Animated,
  DimensionValue,
  Platform,
  Pressable,
  PressableProps,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { createStyles, Theme } from '@/contexts/theme-context';
import { Typography, TypographyVariant } from '@usepepper/ui/atoms/typography';
import { useHover } from 'react-native-web-hooks';
import { useInterpolatedAnimatedStyle } from '@/experiences/sales-portal/blocs/hooks/use-interpolated-animated-style';
import { useMemo, useRef } from 'react';

export enum ButtonSize {
  Large = 'large',
  Medium = 'medium',
  Small = 'small',
}

export enum ButtonVariant {
  Danger = 'danger',
  Primary = 'primary',
  Secondary = 'secondary',
}

const BUTTON_SIZES = {
  [ButtonSize.Small]: 40,
  [ButtonSize.Medium]: 48,
  [ButtonSize.Large]: 56,
};

const BUTTON_LOADING_SIZE = {
  [ButtonSize.Small]: 22,
  [ButtonSize.Medium]: 22,
  [ButtonSize.Large]: 32,
};

const BUTTON_TEXT_VARIANTS: Record<ButtonSize, TypographyVariant> = {
  [ButtonSize.Small]: 'label-md',
  [ButtonSize.Medium]: 'label-md',
  [ButtonSize.Large]: 'label-lg',
};

const BUTTON_BASE_STYLES: Record<ButtonVariant, StyleProp<ViewStyle>> = {
  [ButtonVariant.Danger]: {
    borderWidth: 0,
  },
  [ButtonVariant.Primary]: {
    borderWidth: 0,
  },
  [ButtonVariant.Secondary]: {
    borderStyle: 'solid',
    borderWidth: 1,
  },
};

const PRESSED_OPACITY = 0.7;
const isNative = Platform.OS !== 'web';

const createButtonColorMaps = (theme: Theme) => ({
  backgroundColor: {
    [ButtonVariant.Danger]: {
      default: theme.sk.color.bg.button.error.primary.default,
      disabled: theme.sk.color.bg.button.error.primary.default,
      hover: theme.sk.color.bg.button.error.primary.hover,
    },
    [ButtonVariant.Primary]: {
      default: theme.sk.color.bg.button.primary.default,
      disabled: theme.sk.color.bg.button.primary.disabled,
      hover: theme.sk.color.bg.button.primary.hover,
    },
    [ButtonVariant.Secondary]: {
      default: theme.sk.color.bg.button.secondary.default,
      disabled: theme.sk.color.bg.button.secondary.disabled,
      hover: theme.sk.color.bg.button.secondary.hover,
    },
  },
  borderColor: {
    [ButtonVariant.Danger]: {
      default: 'transparent',
      disabled: 'transparent',
      hover: 'transparent',
    },
    [ButtonVariant.Primary]: {
      default: 'transparent',
      disabled: 'transparent',
      hover: 'transparent',
    },
    [ButtonVariant.Secondary]: {
      default: theme.sk.color.border.button.secondary.default,
      disabled: theme.sk.color.border.button.disabled,
      hover: theme.sk.color.border.button.secondary.default,
    },
  },
  color: {
    [ButtonVariant.Danger]: {
      default: theme.sk.color.text.ondark,
      disabled: theme.sk.color.text.ondark,
      hover: theme.sk.color.text.ondark,
    },
    [ButtonVariant.Primary]: {
      default: theme.sk.color.text.ondark,
      disabled: theme.sk.color.text.ondark,
      hover: theme.sk.color.text.ondark,
    },
    [ButtonVariant.Secondary]: {
      default: theme.sk.color.text.primary,
      disabled: theme.sk.color.text.disabled,
      hover: theme.sk.color.text.primary,
    },
  },
});

export interface ButtonProps extends PressableProps {
  accessibilityLabel: string;
  children: string | React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onPress: () => void;
  size?: ButtonSize;
  style?: StyleProp<ViewStyle>;
  variant?: ButtonVariant;
  width?: DimensionValue;
}

export const Button = ({
  children,
  disabled,
  loading,
  onPress,
  style,
  size = ButtonSize.Medium,
  variant = ButtonVariant.Primary,
  width,
  accessibilityLabel,
  ...buttonProps
}: ButtonProps) => {
  const [styles, theme] = useStyles();

  const buttonRef = useRef<React.ComponentRef<typeof Pressable>>(null);
  const buttonColorMaps = useMemo(() => createButtonColorMaps(theme), [theme]);

  // button hover animations
  const isHovered = useHover(buttonRef);
  const animatedBackgroundColor = useInterpolatedAnimatedStyle(
    isHovered,
    buttonColorMaps.backgroundColor[variant].default,
    buttonColorMaps.backgroundColor[variant].hover,
  );
  const animatedBorderColor = useInterpolatedAnimatedStyle(
    isHovered,
    buttonColorMaps.borderColor[variant].default,
    buttonColorMaps.borderColor[variant].hover,
  );

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      ref={buttonRef}
      style={({ pressed }) => [
        disabled ? styles.buttonContainerDisabled : styles.buttonContainer,
        pressed && !disabled && isNative && styles.buttonContainerPressed,
      ]}
      {...buttonProps}
    >
      <Animated.View
        style={[
          styles.button,
          { height: BUTTON_SIZES[size], width },
          BUTTON_BASE_STYLES[variant],
          disabled
            ? {
                // disabled buttons do not need animation
                backgroundColor:
                  buttonColorMaps.backgroundColor[variant].disabled,
                borderColor: buttonColorMaps.borderColor[variant].disabled,
              }
            : {
                backgroundColor: animatedBackgroundColor,
                borderColor: animatedBorderColor,
              },
          style,
        ]}
      >
        {loading ? (
          <ActivityIndicator
            color={
              disabled
                ? buttonColorMaps.color[variant].disabled
                : isHovered
                  ? buttonColorMaps.color[variant].hover
                  : buttonColorMaps.color[variant].default
            }
            size={BUTTON_LOADING_SIZE[size]}
          />
        ) : typeof children === 'string' ? (
          <Typography
            color={
              disabled
                ? buttonColorMaps.color[variant].disabled
                : isHovered
                  ? buttonColorMaps.color[variant].hover
                  : buttonColorMaps.color[variant].default
            }
            variant={BUTTON_TEXT_VARIANTS[size]}
          >
            {children}
          </Typography>
        ) : (
          children
        )}
      </Animated.View>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  button: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    justifyContent: 'center',
  },
  buttonContainer: {
    cursor: 'pointer',
  },
  buttonContainerDisabled: {
    cursor: 'auto',
  },
  buttonContainerPressed: {
    opacity: PRESSED_OPACITY,
  },
}));
